import { useEffect, useState } from 'react';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { showNotification } from '@mantine/notifications';
import { calculateAgeAtGivenTime } from '@/utils/calculate-age-at-given-time';

interface Age {
  years: number;
  months: number;
  days: number;
}

export const usePatientAge = (
  patient?: Patient,
  encounter?: Encounter
): Age | null => {
  const [age, setAge] = useState<Age | null>(null);
  const medplum = useMedplum();

  useEffect(() => {
    const fetchAge = async () => {
      if (!patient?.id) {
        return;
      }

      try {
        const freshPatient = (await medplum.readResource(
          'Patient',
          patient.id
        )) as Patient;

        if (!freshPatient.birthDate) {
          setAge(null);
          return;
        }

        const birthDate = new Date(freshPatient.birthDate);
        const encounterDate = encounter?.period?.start
          ? new Date(encounter.period.start)
          : undefined;

        const ageObj = calculateAgeAtGivenTime(birthDate, encounterDate);
        setAge(ageObj ?? null);
      } catch (err) {
        showNotification({
          color: 'red',
          title: 'Error fetching patient',
          message: err instanceof Error ? err.message : String(err),
        });
        setAge(null);
      }
    };

    fetchAge();
  }, [patient?.id, encounter?.period?.start, medplum]);

  return age;
};
